import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { eur, VERTICAL_META, VerticalIcon } from "@/lib/verticals";
import BookingDialog from "@/components/BookingDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Star, MapPin, SealCheck, Clock } from "@phosphor-icons/react";

const ProviderProfile = () => {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [open, setOpen] = useState(false);
  const [selService, setSelService] = useState(null);

  useEffect(() => {
    api.get(`/providers/${id}`).then((r) => setP(r.data));
  }, [id]);

  if (!p) return <div className="py-24 text-center text-muted-foreground">Caricamento…</div>;

  const book = (svc) => { setSelService(svc); setOpen(true); };

  return (
    <div className="pb-28 md:pb-12">
      {/* Hero */}
      <div className="relative h-[32vh] md:h-[38vh]">
        <img src={p.cover_image} alt={p.business_name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="relative -mt-14 bg-card rounded-2xl border border-border p-5 md:p-6 shadow-sm">
          <div className="flex items-start gap-4">
            <Avatar className="w-16 h-16 ring-2 ring-primary ring-offset-2">
              <AvatarImage src={p.avatar} />
              <AvatarFallback>{p.business_name[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-heading text-2xl font-bold">{p.business_name}</h1>
                {p.verified && <SealCheck weight="fill" className="text-primary" size={18} />}
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1 flex-wrap">
                <span className="flex items-center gap-1"><MapPin size={14} /> {p.city}</span>
                <span className="flex items-center gap-1"><Star weight="fill" className="text-amber-400" size={14} /> {p.rating > 0 ? p.rating : "Nuovo"} ({p.review_count})</span>
                <Badge variant="secondary" className="gap-1"><VerticalIcon v={p.vertical} size={12} weight="bold" />{VERTICAL_META[p.vertical]?.label}</Badge>
              </div>
            </div>
            <Button className="rounded-full hidden md:flex" data-testid="book-now-desktop" onClick={() => book(null)}>
              Prenota ora
            </Button>
          </div>

          <Tabs defaultValue="services" className="mt-6">
            <TabsList className="bg-secondary">
              <TabsTrigger value="services" data-testid="tab-services">Servizi</TabsTrigger>
              <TabsTrigger value="reviews" data-testid="tab-reviews">Recensioni</TabsTrigger>
              <TabsTrigger value="about" data-testid="tab-about">Info</TabsTrigger>
            </TabsList>

            <TabsContent value="services" className="mt-4 space-y-2">
              {p.services.map((s) => (
                <div key={s.service_id} className="flex items-center justify-between p-4 rounded-xl border border-border hover:border-primary/40 transition-colors">
                  <div>
                    <div className="font-medium">{s.name}</div>
                    <div className="text-sm text-muted-foreground">{s.description}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><Clock size={12} /> {s.duration_min} min</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="font-semibold text-primary mb-1">{eur(s.price)}</div>
                    <Button size="sm" variant="outline" className="rounded-full" data-testid={`book-service-${s.service_id}`} onClick={() => book(s)}>Prenota</Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="reviews" className="mt-4 space-y-3">
              {p.reviews.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Ancora nessuna recensione.</p>}
              {p.reviews.map((r) => (
                <div key={r.review_id} className="p-4 rounded-xl border border-border">
                  <div className="flex items-center gap-2">
                    <Avatar className="w-8 h-8"><AvatarImage src={r.customer_picture} /><AvatarFallback>{r.customer_name?.[0]}</AvatarFallback></Avatar>
                    <span className="font-medium text-sm">{r.customer_name}</span>
                    <span className="flex items-center gap-0.5 ml-auto">
                      {[...Array(5)].map((_, i) => <Star key={i} size={13} weight="fill" className={i < r.rating ? "text-amber-400" : "text-muted"} />)}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm text-muted-foreground mt-2">{r.comment}</p>}
                </div>
              ))}
            </TabsContent>

            <TabsContent value="about" className="mt-4">
              <p className="text-muted-foreground leading-relaxed">{p.bio}</p>
              <div className="mt-4 text-sm flex items-center gap-2 text-muted-foreground"><MapPin size={15} /> {p.address}, {p.city}</div>
            </TabsContent>
          </Tabs>
        </motion.div>
      </div>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-40 glass-nav border-t border-border p-3">
        <Button className="w-full rounded-full h-12" data-testid="book-now-mobile" onClick={() => book(null)}>
          Prenota ora — da {eur(p.min_price || (p.services[0]?.price ?? 0))}
        </Button>
      </div>

      <BookingDialog open={open} onOpenChange={setOpen} provider={p} initialService={selService} />
    </div>
  );
};

export default ProviderProfile;
